import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getNews, trackVisit } from '../lib/api';
import { NewsCardHero, NewsCardSmall } from '../components/NewsCard';
import { Loader2, ChevronLeft, ChevronRight, Megaphone, ArrowRight, Search, X, Archive } from 'lucide-react';

const CATS = ["Política","Deportes","Tecnología","Negocios","Entretenimiento","Cultura"];

export default function HomePage() {
  const [news,setNews]=useState([]);
  const [loading,setLoading]=useState(true);
  const [cat,setCat]=useState(null);
  const [page,setPage]=useState(1);
  const [pages,setPages]=useState(1);
  const [total,setTotal]=useState(0);
  const [q,setQ]=useState('');
  const [qDebounced,setQDebounced]=useState('');

  useEffect(()=>{trackVisit('/');},[]);
  useEffect(()=>{ const t=setTimeout(()=>setQDebounced(q.trim()),400); return ()=>clearTimeout(t); },[q]);
  useEffect(()=>{ setPage(1); },[qDebounced,cat]);

  const fetch_=useCallback(async()=>{
    setLoading(true);
    try{const{data}=await getNews(cat,page,qDebounced);setNews(data.news);setPages(data.pages);setTotal(data.total);}
    catch(e){console.error(e);}finally{setLoading(false);}
  },[cat,page,qDebounced]);
  useEffect(()=>{fetch_();},[fetch_]);

  const hero=news[0]||null;
  const rest=news.slice(1);

  return (
    <div data-testid="home-page" className="min-h-screen bg-white">
      {/* Breaking News Marquee */}
      <div className="bg-[#E50B15] overflow-hidden">
        <div className="animate-marquee whitespace-nowrap py-1.5">
          <span className="text-[10px] font-black text-white tracking-[0.3em] uppercase mx-8">NOTICIAS DE ÚLTIMA HORA</span>
          <span className="text-[10px] font-black text-white/60 tracking-[0.15em] uppercase mx-8">RP NOTICIAS — LA CRÍTICA DE LOS HECHOS</span>
          <span className="text-[10px] font-black text-white tracking-[0.3em] uppercase mx-8">NOTICIAS DE ÚLTIMA HORA</span>
          <span className="text-[10px] font-black text-white/60 tracking-[0.15em] uppercase mx-8">RP NOTICIAS — LA CRÍTICA DE LOS HECHOS</span>
          <span className="text-[10px] font-black text-white tracking-[0.3em] uppercase mx-8">NOTICIAS DE ÚLTIMA HORA</span>
          <span className="text-[10px] font-black text-white/60 tracking-[0.15em] uppercase mx-8">RP NOTICIAS — LA CRÍTICA DE LOS HECHOS</span>
        </div>
      </div>

      {/* Category Bar */}
      <div className="border-b-2 border-[#0B1F40]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center gap-0 overflow-x-auto">
          <button data-testid="filter-all" onClick={()=>{setCat(null);setPage(1);}}
            className={`shrink-0 px-5 py-3 text-[10px] font-black tracking-[0.2em] uppercase border-b-4 transition-all ${!cat?'border-[#E50B15] text-[#E50B15]':'border-transparent text-[#0B1F40]/40 hover:text-[#0B1F40]'}`}>TODAS</button>
          {CATS.map(c=>(
            <button key={c} data-testid={`filter-${c.toLowerCase()}`} onClick={()=>{setCat(c);setPage(1);}}
              className={`shrink-0 px-5 py-3 text-[10px] font-black tracking-[0.2em] uppercase border-b-4 transition-all ${cat===c?'border-[#E50B15] text-[#E50B15]':'border-transparent text-[#0B1F40]/40 hover:text-[#0B1F40]'}`}>{c}</button>
          ))}
        </div>
      </div>

      {/* Masthead */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 border-b-4 border-[#0B1F40]">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <span className="text-[10px] font-black tracking-[0.3em] uppercase text-[#E50B15]">LO ÚLTIMO</span>
            <h1 className="text-5xl sm:text-7xl tracking-tighter text-[#0B1F40] leading-none" style={{fontFamily:'Archivo Black'}}>NOTICIAS</h1>
          </div>
          <div className="flex items-center gap-3 ml-auto">
            {/* Compact Denuncia pill button */}
            <Link to="/denuncia" data-testid="home-denuncia-cta" className="group relative inline-flex items-center gap-2 bg-[#E50B15] text-white text-[10px] font-black tracking-[0.2em] uppercase h-9 pl-3 pr-4 transition-all hover:bg-[#0B1F40]" style={{boxShadow:'3px 3px 0 #0B1F40'}}>
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
              </span>
              <Megaphone className="h-3.5 w-3.5" />
              <span>DENUNCIA ANÓNIMA</span>
              <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
            </Link>
            <span className="hidden sm:block text-[10px] text-[#0B1F40]/25 tracking-[0.15em] uppercase font-bold">
              {new Date().toLocaleDateString('es-ES',{weekday:'long',day:'numeric',month:'long',year:'numeric'})}
            </span>
          </div>
        </div>
      </div>

      {/* Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Search bar */}
        <div className="mb-6 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#0B1F40]/30 pointer-events-none" />
            <input data-testid="news-search" value={q} onChange={e=>setQ(e.target.value)} placeholder="Buscar noticias..."
              className="w-full h-10 pl-9 pr-9 border-2 border-[#0B1F40]/20 focus:border-[#E50B15] focus:outline-none text-sm font-bold text-[#0B1F40] placeholder:text-[#0B1F40]/30" />
            {q && <button onClick={()=>setQ('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#0B1F40]/40 hover:text-[#E50B15]"><X className="h-3.5 w-3.5" /></button>}
          </div>
          <Link to="/archivo" data-testid="nav-archivo" className="inline-flex items-center gap-1.5 h-10 px-4 border-2 border-[#0B1F40]/20 text-[10px] font-black tracking-[0.2em] uppercase text-[#0B1F40] hover:border-[#0B1F40] hover:bg-[#0B1F40] hover:text-white transition-colors">
            <Archive className="h-3 w-3" /> HEMEROTECA
          </Link>
          {qDebounced && !loading && (
            <span data-testid="search-results-count" className="text-[10px] font-black tracking-wider text-[#0B1F40]/50 uppercase">
              {total} RESULTADO{total===1?'':'S'} PARA "{qDebounced}"
            </span>
          )}
        </div>
        {loading&&<div className="flex justify-center py-32"><Loader2 className="h-7 w-7 animate-spin text-[#E50B15]"/></div>}
        {!loading&&news.length===0&&(
          <div className="text-center py-32 border-2 border-dashed border-[#0B1F40]/15">
            <p className="text-[#0B1F40]/40 text-lg">No hay noticias</p>
            {cat&&<button onClick={()=>setCat(null)} className="mt-3 text-[#E50B15] text-sm font-black underline underline-offset-4">Ver todas</button>}
          </div>
        )}
        {!loading&&news.length>0&&(
          <>
            <div className="grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-6">
              {hero&&<NewsCardHero news={hero}/>}
              {rest.map((n,i)=>(
                <div key={n.id} className={`col-span-1 md:col-span-4 animate-fade-in stagger-${i+1}`}>
                  <NewsCardSmall news={n}/>
                </div>
              ))}
            </div>
            {pages>1&&(
              <div data-testid="pagination" className="flex items-center justify-center gap-4 mt-12 pt-8 border-t-2 border-[#0B1F40]">
                <button data-testid="prev-page" disabled={page<=1} onClick={()=>setPage(p=>p-1)}
                  className="bg-[#0B1F40] text-white text-[10px] font-black h-9 px-5 disabled:opacity-30 brutal-btn-press transition-all" style={{boxShadow:'3px 3px 0px #E50B15'}}>
                  <ChevronLeft className="h-3.5 w-3.5 inline mr-1"/>ANTERIOR
                </button>
                <span className="text-[10px] font-black text-[#0B1F40]/40 tracking-wider">{page}/{pages}</span>
                <button data-testid="next-page" disabled={page>=pages} onClick={()=>setPage(p=>p+1)}
                  className="bg-[#0B1F40] text-white text-[10px] font-black h-9 px-5 disabled:opacity-30 brutal-btn-press transition-all" style={{boxShadow:'3px 3px 0px #E50B15'}}>
                  SIGUIENTE<ChevronRight className="h-3.5 w-3.5 inline ml-1"/>
                </button>
              </div>
            )}
          </>
        )}
      </section>

      {/* Floating Denuncia FAB (bottom-right) */}
      <Link to="/denuncia" data-testid="denuncia-fab"
        className="fixed bottom-6 right-6 z-40 group flex items-center gap-2 bg-[#E50B15] text-white h-12 pl-3 pr-4 hover:bg-[#0B1F40] transition-all"
        style={{boxShadow:'4px 4px 0 #0B1F40'}}>
        <span className="relative flex h-2.5 w-2.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-white"></span>
        </span>
        <Megaphone className="h-4 w-4" />
        <span className="text-[10px] font-black tracking-[0.2em] uppercase hidden sm:inline">DENUNCIA</span>
      </Link>
    </div>
  );
}
